import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'

/**
 * The bid's click-to-index chunk ceiling (plans/BID_SITE_INDEXING.md) — a guardrail against an
 * unattended bid growing its own knowledge base without bound, one click-to-index run at a time.
 *
 * Everyone on the bid sees how much of the ceiling is already used — knowing it is information,
 * not a privilege, the same "everyone sees, only the right person changes" split as BrandingCard.
 * Raising it is stricter still: `useBidProfile().isTenantAdmin`, NOT the bid's own `admin` profile
 * — the server enforces this regardless (`identity.roles`), so the client check here only avoids
 * offering a control that would 403.
 */
export function IndexLimitCard({ bid }: { bid: string }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const { isTenantAdmin } = useBidProfile(bid)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const query = useQuery({
    queryKey: ['index-limit', bid],
    queryFn: () => api.indexLimit(bid),
    enabled: Boolean(bid),
  })
  const data = query.data

  async function save(): Promise<void> {
    const maxChunks = Number(draft)
    if (!Number.isInteger(maxChunks) || maxChunks <= 0) {
      setError(t('indexlimit_invalid'))
      return
    }
    setSaving(true)
    setError('')
    try {
      await api.setIndexLimit(bid, maxChunks)
      await qc.invalidateQueries({ queryKey: ['index-limit', bid] })
      setDraft('')
    } catch (e) {
      setError(isApiError(e) && e.serverMessage ? e.serverMessage : t('indexlimit_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="indexlimit-card" aria-label={t('indexlimit_title')}>
      <div className="indexlimit-head">
        <span className="home-eyebrow">{t('indexlimit_title')}</span>
      </div>
      {query.isLoading ? (
        <p aria-live="polite">…</p>
      ) : (
        <>
          <p className="indexlimit-usage">
            {t('indexlimit_used')
              .replace('{used}', String(data?.used ?? 0))
              .replace('{max}', String(data?.maxChunks ?? 0))}
          </p>
          <p className="indexlimit-hint">{t('indexlimit_hint')}</p>

          {isTenantAdmin && (
            <div className="indexlimit-form">
              <label className="indexlimit-field">
                <span>{t('indexlimit_input_label')}</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  placeholder={String(data?.maxChunks ?? '')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="mini ok"
                disabled={saving || draft.trim() === ''}
                onClick={() => void save()}
              >
                {t('indexlimit_save')}
              </button>
            </div>
          )}

          {error && (
            <p className="indexlimit-error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </section>
  )
}
